import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import API, { setAccessToken, clearAccessToken, getAccessToken } from "@/lib/api";
import { tryBiometricUnlock } from "@/lib/biometric";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await API.get("/auth/me");
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data);
      return data;
    } catch {
      setUser(false);
      clearAccessToken();
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /auth/me check.
    // ProviderAuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // On cold launch, if we have no cached token, try biometric unlock first (Capacitor only).
    (async () => {
      if (!getAccessToken()) {
        const unlocked = await tryBiometricUnlock();
        if (unlocked?.token) {
          setAccessToken(unlocked.token);
        }
      }
      checkAuth();
    })();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await API.post("/auth/login", { email, password });
    if (data.access_token) setAccessToken(data.access_token);
    setUser(data);
    return data;
  };

  const register = async (email, password, full_name, phone) => {
    const { data } = await API.post("/auth/register", { email, password, full_name, phone });
    if (data.access_token) setAccessToken(data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    await API.post("/auth/logout").catch(() => {});
    // Wipe the on-device biometric credential so the next launch returns to /login
    try {
      const { disableBiometric } = await import("@/lib/biometric");
      await disableBiometric();
    } catch { /* ignore */ }
    clearAccessToken();
    setUser(false);
  };

  const updateProfile = async (updates) => {
    const { data } = await API.put("/auth/me", updates);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
